import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    Divider,
    FormControlLabel,
    LinearProgress,
    List,
    ListItem,
    ListItemText,
    MenuItem,
    Modal,
    Radio,
    RadioGroup,
    Snackbar,
    Tab,
    Tabs,
    Typography,
} from '@mui/material';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { useDispatch, useSelector } from 'react-redux';
import ModalBox from '../themes/overrides/ModalBox';
import TextFieldInput from '../themes/overrides/TextFieldInput';
import LoadingOverlay from './LoadingOverlay';
import {
    getEnvironments,
    transferContent,
    waitForEnvironmentJob,
} from '../actions/environmentAction';
import { fetchContentTypesInEnvironment, fetchEntriesInEnvironment } from '../api/api';

/**
 * Copy or move content models and entries between two environments.
 *
 * The transfer is always previewed first: the API returns the exact plan —
 * what it would create, what it would update, and anything it would have to
 * drop — and nothing is written until the plan is applied.
 */
const TransferContentModal = ({ open, onClose, workspaceId, onSuccess }) => {
    const dispatch = useDispatch();
    const environments = useSelector((state) => state.environments.environments);

    const [source, setSource] = useState('production');
    const [target, setTarget] = useState('');
    const [tab, setTab] = useState(0);
    const [mode, setMode] = useState('copy');
    const [includeReferences, setIncludeReferences] = useState(true);

    const [contentTypes, setContentTypes] = useState([]);
    const [selectedContentTypes, setSelectedContentTypes] = useState([]);
    const [allContentTypes, setAllContentTypes] = useState(false);

    const [entries, setEntries] = useState([]);
    const [entrySearch, setEntrySearch] = useState('');
    const [selectedEntries, setSelectedEntries] = useState([]);
    const [allEntries, setAllEntries] = useState(false);
    const [entriesOfSelectedModels, setEntriesOfSelectedModels] = useState(false);

    const [plan, setPlan] = useState(null);
    const [job, setJob] = useState(null);
    const [loading, setLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const notify = (message, severity = 'success') =>
        setSnackbar({ open: true, message, severity });

    // The poll loop outlives a render, so it reads "is this still open?" off a
    // ref rather than the captured prop.
    const openRef = useRef(open);
    useEffect(() => {
        openRef.current = open;
    }, [open]);

    useEffect(() => {
        if (!open) return;
        if (workspaceId) dispatch(getEnvironments(workspaceId, true));
        // Reopening starts clean: a finished — or timed-out — job from last
        // time would otherwise keep the apply button disabled.
        setPlan(null);
        setJob(null);
    }, [open, workspaceId, dispatch]);

    useEffect(() => {
        if (!target && environments.length > 1) {
            setTarget(environments.find((environment) => environment.slug !== source)?.slug ?? '');
        }
    }, [environments, source, target]);

    /* The pickers always show the SOURCE environment's content, whichever
       environment the dashboard itself is currently pointed at. */
    const loadSourceContent = useCallback(async () => {
        if (!open || !workspaceId || !source) return;
        setLoading(true);
        try {
            const [contentTypeRes, entryRes] = await Promise.all([
                fetchContentTypesInEnvironment(workspaceId, source),
                fetchEntriesInEnvironment(workspaceId, source, {
                    page: 1,
                    ...(entrySearch ? { identifier: entrySearch } : {}),
                }),
            ]);
            setContentTypes(Array.isArray(contentTypeRes?.data) ? contentTypeRes.data : []);
            setEntries(entryRes?.data?.entries ?? []);
        } catch (error) {
            notify(error?.response?.data?.message ?? 'Could not read the source environment', 'error');
        } finally {
            setLoading(false);
        }
    }, [open, workspaceId, source, entrySearch]);

    useEffect(() => {
        loadSourceContent();
    }, [loadSourceContent]);

    // Selections belong to one source environment; switching source invalidates them.
    useEffect(() => {
        setSelectedContentTypes([]);
        setSelectedEntries([]);
    }, [source, target]);

    const payload = useMemo(
        () => ({
            source,
            target,
            mode,
            includeReferences,
            allContentTypes,
            contentTypeIds: allContentTypes ? [] : selectedContentTypes,
            allEntries,
            entriesOfSelectedModels,
            entryIds: allEntries ? [] : selectedEntries,
        }),
        [
            source,
            target,
            mode,
            includeReferences,
            allContentTypes,
            selectedContentTypes,
            allEntries,
            entriesOfSelectedModels,
            selectedEntries,
        ],
    );

    // ANY change to what would be transferred invalidates the preview. Applying
    // is gated on having a plan, so a stale one let the user approve one set of
    // changes and then apply a different one — including flipping Copy to Move,
    // which deletes the source.
    useEffect(() => {
        setPlan(null);
        setJob(null);
    }, [payload]);

    const nothingSelected =
        !allContentTypes &&
        !allEntries &&
        selectedContentTypes.length === 0 &&
        selectedEntries.length === 0;

    const run = async (dryRun) => {
        if (!target || source === target) {
            notify('Pick two different environments', 'error');
            return;
        }
        setLoading(true);
        try {
            const result = await dispatch(transferContent(workspaceId, { ...payload, dryRun }));

            // A dry run only reads, so it answers with the plan inline.
            if (dryRun) {
                if (result?.status !== 'success') {
                    notify(result?.message ?? 'Could not build the preview', 'error');
                    return;
                }
                setPlan(result.plan);
                setJob(null);
                return;
            }

            // Applying is queued: the write pass is per entry, per locale and
            // per reference, so it cannot be held open on a request.
            if (result?.status !== 'accepted' || !result?.jobId) {
                notify(result?.message ?? 'Transfer failed', 'error');
                return;
            }

            setJob({ state: 'queued', processed: 0, total: null });
            setLoading(false);

            const finished = await waitForEnvironmentJob(workspaceId, result.jobId, {
                onProgress: setJob,
                isCancelled: () => !openRef.current,
            });

            if (!finished || !openRef.current) return;

            setJob(finished);
            if (finished.state === 'failed') {
                notify(finished.error ?? 'Transfer failed', 'error');
                return;
            }
            notify(
                `${finished.result.entriesCreated} created, ${finished.result.entriesUpdated} updated, ` +
                `${finished.result.entriesRemoved} removed`,
            );
            onSuccess?.();
        } catch (error) {
            notify(error?.response?.data?.message ?? error?.message ?? 'Transfer failed', 'error');
        } finally {
            setLoading(false);
        }
    };

    const jobRunning = job?.state === 'queued' || job?.state === 'running';

    const toggle = (list, setList, id) =>
        setList(list.includes(id) ? list.filter((item) => item !== id) : [...list, id]);

    return (
        <Modal open={open} onClose={onClose}>
            <ModalBox sx={{ width: '860px', maxWidth: '96%' }}>
                <Typography variant="h2" sx={{ mb: 1 }}>Move content between environments</Typography>
                <Typography sx={{ color: '#ACACAC', mb: 3 }}>
                    Content models are matched by their slug and entries by their lineage, so running
                    the same transfer twice updates what is already there instead of duplicating it.
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                    <TextFieldInput
                        select
                        label="From"
                        value={source}
                        onChange={(event) => setSource(event.target.value)}
                        sx={{ flex: '1 1 200px' }}
                    >
                        {environments.map((environment) => (
                            <MenuItem key={environment.slug} value={environment.slug}>
                                {environment.name}
                            </MenuItem>
                        ))}
                    </TextFieldInput>

                    <SwapHorizIcon sx={{ color: '#94a3b8' }} />

                    <TextFieldInput
                        select
                        label="To"
                        value={target}
                        onChange={(event) => setTarget(event.target.value)}
                        sx={{ flex: '1 1 200px' }}
                    >
                        {environments
                            .filter((environment) => environment.slug !== source)
                            .map((environment) => (
                                <MenuItem key={environment.slug} value={environment.slug}>
                                    {environment.name}
                                </MenuItem>
                            ))}
                    </TextFieldInput>
                </Box>

                <Tabs value={tab} onChange={(event, value) => setTab(value)} sx={{ mb: 2 }}>
                    <Tab label={`Content models (${allContentTypes ? contentTypes.length : selectedContentTypes.length})`} />
                    <Tab label={`Entries (${allEntries ? 'all' : selectedEntries.length})`} />
                </Tabs>

                {tab === 0 && (
                    <Box>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={allContentTypes}
                                    onChange={(event) => setAllContentTypes(event.target.checked)}
                                />
                            }
                            label="Every content model in the source environment"
                        />
                        <List dense sx={{ maxHeight: 240, overflowY: 'auto' }}>
                            {contentTypes.map((contentType) => (
                                <ListItem key={contentType._id} sx={{ px: 0 }}>
                                    <Checkbox
                                        disabled={allContentTypes}
                                        checked={allContentTypes || selectedContentTypes.includes(contentType._id)}
                                        onChange={() =>
                                            toggle(selectedContentTypes, setSelectedContentTypes, contentType._id)
                                        }
                                    />
                                    <ListItemText primary={contentType.name} secondary={contentType.slug} />
                                </ListItem>
                            ))}
                            {contentTypes.length === 0 && (
                                <Typography sx={{ color: '#94a3b8', px: 1 }}>
                                    No content models in this environment.
                                </Typography>
                            )}
                        </List>
                    </Box>
                )}

                {tab === 1 && (
                    <Box>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={allEntries}
                                    onChange={(event) => setAllEntries(event.target.checked)}
                                />
                            }
                            label="Every entry in the source environment"
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    disabled={allEntries || allContentTypes || selectedContentTypes.length === 0}
                                    checked={entriesOfSelectedModels}
                                    onChange={(event) => setEntriesOfSelectedModels(event.target.checked)}
                                />
                            }
                            label="Every entry of the selected content models"
                        />
                        <TextFieldInput
                            label="Search entries"
                            value={entrySearch}
                            onChange={(event) => setEntrySearch(event.target.value)}
                            fullWidth
                            sx={{ my: 2 }}
                        />
                        <List dense sx={{ maxHeight: 220, overflowY: 'auto' }}>
                            {entries.map((entry) => (
                                <ListItem key={entry._id} sx={{ px: 0 }}>
                                    <Checkbox
                                        disabled={allEntries}
                                        checked={allEntries || selectedEntries.includes(String(entry._id))}
                                        onChange={() =>
                                            toggle(selectedEntries, setSelectedEntries, String(entry._id))
                                        }
                                    />
                                    <ListItemText
                                        primary={entry.identifier}
                                        secondary={`${entry.content_type_name ?? ''}${entry.published ? ' · published' : ' · draft'}`}
                                    />
                                </ListItem>
                            ))}
                            {entries.length === 0 && (
                                <Typography sx={{ color: '#94a3b8', px: 1 }}>
                                    No entries match.
                                </Typography>
                            )}
                        </List>
                    </Box>
                )}

                <Divider sx={{ my: 2 }} />

                <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
                    <RadioGroup row value={mode} onChange={(event) => setMode(event.target.value)}>
                        <FormControlLabel value="copy" control={<Radio />} label="Copy" />
                        <FormControlLabel value="move" control={<Radio />} label="Move (deletes the source)" />
                    </RadioGroup>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={includeReferences}
                                onChange={(event) => setIncludeReferences(event.target.checked)}
                            />
                        }
                        label="Bring referenced entries along"
                    />
                </Box>

                {plan && !job && (
                    <Box sx={{ mt: 2, p: 2, border: '1px solid #ECECEC', borderRadius: 2 }}>
                        <Typography variant="h6" sx={{ mb: 1 }}>Preview</Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                            <Chip
                                size="small"
                                label={`${plan.contentTypes.filter((item) => item.action === 'create').length} model(s) to create`}
                            />
                            <Chip
                                size="small"
                                label={`${plan.contentTypes.filter((item) => item.action === 'update').length} model(s) to update`}
                            />
                            <Chip
                                size="small"
                                label={`${plan.entries.filter((item) => item.action === 'create').length} entry(s) to create`}
                            />
                            <Chip
                                size="small"
                                label={`${plan.entries.filter((item) => item.action === 'update').length} entry(s) to update`}
                            />
                        </Box>
                        {plan.warnings?.length > 0 && (
                            <Alert severity="warning" sx={{ mt: 1 }}>
                                <ul style={{ margin: 0, paddingLeft: '1.2em' }}>
                                    {plan.warnings.slice(0, 8).map((warning) => (
                                        <li key={warning}>{warning}</li>
                                    ))}
                                </ul>
                            </Alert>
                        )}
                    </Box>
                )}

                {/* The transfer itself runs on the queue. Its own counts are
                    reported here rather than the plan's, because the plan is
                    rebuilt when the job runs — what was written is what
                    matters, not what was predicted. */}
                {job && (
                    <Box sx={{ mt: 2, p: 2, border: '1px solid #ECECEC', borderRadius: 2 }}>
                        <Typography variant="h6" sx={{ mb: 1 }}>
                            {jobRunning
                                ? 'Transfer in progress'
                                : job.state === 'failed'
                                    ? 'Transfer failed'
                                    : 'Transfer complete'}
                        </Typography>

                        {jobRunning && (
                            <>
                                <Typography variant="body2" sx={{ color: '#94a3b8', mb: 1 }}>
                                    {job.total
                                        ? `${job.processed ?? 0} of ${job.total} entries`
                                        : 'Working out what to transfer…'}
                                    {' · you can leave this open, it keeps running either way'}
                                </Typography>
                                <LinearProgress
                                    variant={job.total ? 'determinate' : 'indeterminate'}
                                    value={job.total ? ((job.processed ?? 0) / job.total) * 100 : undefined}
                                />
                            </>
                        )}

                        {job.state === 'completed' && job.result && (
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                                <Chip size="small" label={`${job.result.contentTypesCreated} model(s) created`} />
                                <Chip size="small" label={`${job.result.contentTypesUpdated} model(s) updated`} />
                                <Chip size="small" label={`${job.result.entriesCreated} entry(s) created`} />
                                <Chip size="small" label={`${job.result.entriesUpdated} entry(s) updated`} />
                                {job.result.entriesRemoved > 0 && (
                                    <Chip size="small" color="error" label={`${job.result.entriesRemoved} entry(s) removed`} />
                                )}
                            </Box>
                        )}

                        {job.state === 'failed' && (
                            <Alert severity="error" sx={{ mt: 1 }}>
                                {job.error ?? 'The transfer did not finish.'}
                                {' '}
                                Nothing was duplicated by the attempt — models are matched by slug
                                and entries by lineage, so running it again picks up where it left off.
                            </Alert>
                        )}

                        {job.warnings?.length > 0 && (
                            <Alert severity="warning" sx={{ mt: 1 }}>
                                <ul style={{ margin: 0, paddingLeft: '1.2em' }}>
                                    {job.warnings.slice(0, 8).map((warning) => (
                                        <li key={warning}>{warning}</li>
                                    ))}
                                </ul>
                            </Alert>
                        )}
                    </Box>
                )}

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
                    <Button onClick={onClose}>Close</Button>
                    <Button
                        variant="outlined"
                        onClick={() => run(true)}
                        disabled={loading || jobRunning || nothingSelected || !target}
                    >
                        Preview changes
                    </Button>
                    <Button
                        variant="contained"
                        color={mode === 'move' ? 'error' : 'primary'}
                        onClick={() => run(false)}
                        disabled={
                            loading ||
                            jobRunning ||
                            nothingSelected ||
                            !target ||
                            !plan ||
                            job?.state === 'completed'
                        }
                    >
                        {mode === 'move' ? 'Move content' : 'Copy content'}
                    </Button>
                </Box>

                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={6000}
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
                        {snackbar.message}
                    </Alert>
                </Snackbar>

                {loading && <LoadingOverlay />}
            </ModalBox>
        </Modal>
    );
};

TransferContentModal.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func,
    workspaceId: PropTypes.string,
    onSuccess: PropTypes.func,
};

export default TransferContentModal;
